import React from "react";
import { Select } from "antd";

import useInvestmentCurrencies, {
  TInvestmentCurrency,
} from "../../../../custom-hooks/useInvestmentCurrencies";
import { Alert } from "./icons";
import styles from "./fundForm.module.scss";

export type TCurrencyChoice = { value: string; label: string };

interface ICurrencyPicker {
  selected: TCurrencyChoice[];
  onChange: (next: TCurrencyChoice[]) => void;
  disabled?: boolean;
}

/**
 * The currencies a fund accepts.
 *
 * Owns its own fetch. It used to be a bare promise handed to an async select,
 * which React Query never saw — so a request that failed once, on a dropped
 * connection, was never retried and never refetched. The dropdown stayed empty
 * for the rest of the session while every other list on the page came back by
 * itself. And an empty dropdown looks like a fund with no currencies rather
 * than a list that failed to load, which is why the failure is now said out
 * loud with a way to try again.
 */
export default function CurrencyPicker({
  selected,
  onChange,
  disabled,
}: Readonly<ICurrencyPicker>) {
  const { data, isLoading, isError, isFetching, refetch } =
    useInvestmentCurrencies();

  const fetched = (data?.data ?? []).map((currency: TInvestmentCurrency) => ({
    value: currency.currency_code,
    label: currency.currency_name,
  }));

  /*
   * A currency the fund already has stays in the list whether or not the
   * fetch came back. Without it a failed load turns "Naira" into "NGN" on a
   * saved fund, because antd falls back to the raw value when it has no
   * matching option to read a label from.
   */
  const byCode = new Map(
    [...selected, ...fetched].map((option) => [option.value, option]),
  );
  const options = Array.from(byCode.values());

  return (
    <div className={styles.currencyPicker}>
      <div className={styles.control}>
        <Select
          mode="multiple"
          aria-label="Currencies"
          style={{ width: "100%" }}
          placeholder={isError ? "Currencies unavailable" : "Add a currency"}
          status={isError ? "error" : undefined}
          loading={isLoading}
          disabled={disabled}
          options={options}
          value={selected.map((choice) => choice.value)}
          onChange={(codes: string[]) =>
            onChange(
              codes.map(
                (code) =>
                  byCode.get(code) ??
                  // A currency already on the fund that the list has not
                  // come back with yet keeps the name it was saved under.
                  selected.find((choice) => choice.value === code) ?? {
                    value: code,
                    label: code,
                  },
              ),
            )
          }
          filterOption={(input, option) =>
            String(option?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      </div>

      {isError ? (
        <div className={styles.currencyPickerError}>
          <Alert size={14} color="#ce0000" />
          Couldn&apos;t load currencies.
          <button
            type="button"
            className={styles.linkButton}
            disabled={isFetching}
            onClick={() => refetch()}
          >
            {isFetching ? "Trying…" : "Try again"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
